import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import MovieCard from '../../components/MovieCard/MovieCard';
import { searchMovies, moviesDatabase } from '../../utils/database';
import './SearchPage.css';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [searchResults, setSearchResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    genre: '',
    year: '',
    rating: '',
    sortBy: 'relevance'
  });

  // Get unique values for filters
  const genres = [...new Set(moviesDatabase.flatMap(movie => movie.categories))];
  const years = [...new Set(moviesDatabase.map(movie => movie.year))].sort((a, b) => b - a);

  useEffect(() => {
    handleSearch(query);
  }, [query]);

  useEffect(() => {
    applyFilters();
  }, [filters, searchResults]);

  const handleSearch = (searchTerm) => {
    if (!searchTerm) {
      setSearchResults(moviesDatabase);
      return;
    }

    setIsLoading(true);
    // Simulate API delay
    setTimeout(() => {
      const results = searchMovies(searchTerm);
      setSearchResults(results);
      setIsLoading(false);
    }, 500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
      handleSearch(searchQuery);
    }
  };

  const applyFilters = () => {
    let results = [...searchResults];

    // Apply genre filter
    if (filters.genre) {
      results = results.filter(movie => 
        movie.categories.includes(filters.genre)
      );
    }

    // Apply year filter
    if (filters.year) {
      results = results.filter(movie => movie.year === filters.year);
    }

    // Apply rating filter
    if (filters.rating) {
      const minRating = parseFloat(filters.rating);
      results = results.filter(movie => movie.rating >= minRating);
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'rating':
        results.sort((a, b) => b.rating - a.rating);
        break;
      case 'year':
        results.sort((a, b) => b.year - a.year);
        break;
      case 'title':
        results.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        // Keep relevance order
        break;
    }

    setFilteredResults(results);
  };

  const resetFilters = () => {
    setFilters({
      genre: '',
      year: '',
      rating: '',
      sortBy: 'relevance'
    });
  };

  const displayResults = filteredResults.length > 0 ? filteredResults : searchResults;

  return (
    <div className="search-page">
      <div className="search-page__header">
        <div className="search-page__container">
          <form onSubmit={handleSubmit} className="search-page__form">
            <div className="search-page__input-wrapper">
              <SearchIcon className="search-page__input-icon" />
              <input
                type="text"
                className="search-page__input"
                placeholder="Search movies, actors, directors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-page__clear"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchParams({});
                    setSearchResults(moviesDatabase);
                  }}
                >
                  <CloseIcon />
                </button>
              )}
            </div>
            <button type="submit" className="search-page__submit">
              Search
            </button>
          </form>

          <button 
            className="search-page__filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterListIcon />
            <span>Filters</span>
            {Object.values(filters).filter(v => v).length > 0 && (
              <span className="search-page__filter-count">
                {Object.values(filters).filter(v => v).length}
              </span>
            )}
          </button>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              className="search-page__filters"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="search-page__filters-inner">
                <div className="search-page__filter-group">
                  <label className="search-page__filter-label">Genre</label>
                  <select
                    className="search-page__filter-select"
                    value={filters.genre}
                    onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
                  >
                    <option value="">All Genres</option>
                    {genres.map(genre => (
                      <option key={genre} value={genre}>{genre}</option>
                    ))}
                  </select>
                </div>

                <div className="search-page__filter-group">
                  <label className="search-page__filter-label">Year</label>
                  <select
                    className="search-page__filter-select"
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                  >
                    <option value="">All Years</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div className="search-page__filter-group">
                  <label className="search-page__filter-label">Min Rating</label>
                  <select
                    className="search-page__filter-select"
                    value={filters.rating}
                    onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
                  >
                    <option value="">Any Rating</option>
                    <option value="9">9+ ⭐</option>
                    <option value="8">8+ ⭐</option>
                    <option value="7">7+ ⭐</option>
                    <option value="6">6+ ⭐</option>
                  </select>
                </div>

                <div className="search-page__filter-group">
                  <label className="search-page__filter-label">Sort By</label>
                  <select
                    className="search-page__filter-select"
                    value={filters.sortBy}
                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                  >
                    <option value="relevance">Relevance</option>
                    <option value="rating">Rating</option>
                    <option value="year">Year</option>
                    <option value="title">Title</option>
                  </select>
                </div>

                <button 
                  className="search-page__filter-reset"
                  onClick={resetFilters}
                >
                  Reset Filters
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="search-page__content">
        <div className="search-page__info">
          {query && (
            <h1 className="search-page__title">
              Search results for "{query}"
            </h1>
          )}
          <p className="search-page__count">
            {displayResults.length} {displayResults.length === 1 ? 'movie' : 'movies'} found
          </p>
        </div>

        {isLoading ? (
          <div className="search-page__loading">
            <div className="search-page__spinner"></div>
            <p>Searching...</p>
          </div>
        ) : displayResults.length > 0 ? (
          <motion.div 
            className="search-page__grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {displayResults.map((movie, index) => (
              <MovieCard
                key={movie.id}
                movie={movie}
                index={index}
                variant="poster"
              />
            ))}
          </motion.div>
        ) : (
          <div className="search-page__empty">
            <SearchIcon style={{ fontSize: 80, opacity: 0.3 }} />
            <h2>No movies found</h2>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;